import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getNotifPrefs, playChime } from "../lib/notifPrefs";
import { Bell, CheckCheck, Clock3, ExternalLink } from "lucide-react";

const SEV_DOT = { info: "bg-[#1565C0]", warning: "bg-[#E65100]", critical: "bg-[#C62828]" };
const CATEGORIES = [["all", "All"], ["clinical", "Clinical"], ["task", "Tasks"], ["schedule", "Schedule"], ["portal", "Portal"], ["billing", "Billing"], ["expiring", "Expiring"], ["system", "System"]];

function dateGroup(iso) {
  const d = dayjs(iso);
  if (d.isSame(dayjs(), "day")) return "Today";
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
  return d.format("MMMM D, YYYY");
}

export function AlertsBell() {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState("all");
  const [data, setData] = useState({ alerts: [], unread_count: 0 });
  const navigate = useNavigate();
  const { user } = useAuth();
  const ref = useRef(null);
  const seenIds = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;

  const notifPrefs = () => {
    const p = getNotifPrefs();
    const srv = userRef.current?.preferences?.notifications;
    return srv ? { ...p, ...srv, categories: { ...p.categories, ...(srv.categories || {}) } } : p;
  };

  const load = () => api.get("/alerts").then((r) => {
    setData(r.data);
    const unread = r.data.alerts.filter((a) => !a.read);
    const prefs = notifPrefs();
    if (seenIds.current && prefs.enabled && "Notification" in window && Notification.permission === "granted") {
      const fresh = unread.filter((a) => !seenIds.current.has(a.id) && prefs.categories[a.category || "system"] !== false).slice(0, 3);
      fresh.forEach((a) => {
        try {
          const n = new Notification(`Cocoa EHR - ${a.title}`, { body: a.body || a.severity, tag: a.id });
          n.onclick = () => { window.focus(); navigate(a.link || "/letters"); n.close(); };
        } catch {}
      });
      if (fresh.length && prefs.sound) playChime();
    }
    seenIds.current = new Set(r.data.alerts.map((a) => a.id));
  }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (open && "Notification" in window && Notification.permission === "default") Notification.requestPermission();
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const markRead = async (a) => { if (!a.read) { await api.post(`/alerts/${a.id}/read`).catch(() => {}); load(); } };
  const markAll = async () => { await api.post("/alerts/read-all").catch(() => {}); load(); };
  const snooze = async (a, e) => { e.stopPropagation(); await api.post(`/alerts/${a.id}/snooze`, { minutes: 60 }).catch(() => {}); load(); };
  const act = (a, e) => { e.stopPropagation(); markRead(a); setOpen(false); navigate(a.link || "/letters"); };

  const filtered = data.alerts.filter((a) => cat === "all" || (a.category || "system") === cat);
  const groups = filtered.reduce((acc, a) => { const g = dateGroup(a.created_at); (acc[g] = acc[g] || []).push(a); return acc; }, {});

  return (
    <div className="relative" ref={ref}>
      <button data-testid="header-alerts-btn" aria-label="Notifications" onClick={() => setOpen(!open)} className="p-2 rounded-full hover:bg-white/10 transition-colors relative">
        <Bell size={18} />
        {data.unread_count > 0 && (
          <span data-testid="alerts-unread-badge" className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#FFB300] px-1 text-[9px] font-bold text-[#3E2723]">
            {data.unread_count > 9 ? "9+" : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[26rem] rounded-2xl bg-white text-[#3E2723] shadow-[0_12px_40px_-8px_rgba(62,39,35,0.35)] border border-[#EFEBE0] z-[80]" data-testid="alerts-dropdown">
          <div className="flex items-center justify-between border-b border-[#F1EEE7] px-4 py-2.5">
            <span className="text-sm font-bold">Notifications</span>
            {data.unread_count > 0 && (
              <button data-testid="alerts-mark-all-btn" onClick={markAll} className="flex items-center gap-1 text-[11px] font-semibold text-[#7B6157] hover:text-[#4E342E]"><CheckCheck size={13} /> Mark all read</button>
            )}
          </div>
          <div className="flex gap-1 border-b border-[#F1EEE7] px-3 py-2 overflow-x-auto">
            {CATEGORIES.map(([k, l]) => (
              <button key={k} data-testid={`alerts-cat-${k}`} onClick={() => setCat(k)}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold ${cat === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>{l}</button>
            ))}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#84675B]">No notifications</div>
            ) : Object.entries(groups).map(([g, items]) => (
              <div key={g}>
                <div className="sticky top-0 bg-[#FDFCF8] px-4 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#84675B]" data-testid={`alerts-group-${g === "Today" ? "today" : g === "Yesterday" ? "yesterday" : "older"}`}>{g}</div>
                {items.map((a) => (
                  <div key={a.id} data-testid={`bell-alert-${a.id}`} onClick={() => markRead(a)}
                    className={`flex w-full items-start gap-2.5 px-4 py-2.5 text-left hover:bg-[#FDFCF8] cursor-pointer ${a.read ? "opacity-60" : ""}`}>
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEV_DOT[a.severity] || SEV_DOT.info}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{a.title}</span>
                      {a.body && <span className="block text-xs text-[#7B6157] line-clamp-2">{a.body}</span>}
                      <span className="block text-[10px] text-[#84675B]">{dayjs(a.created_at).format("h:mm A")} · {a.category || "system"}</span>
                    </span>
                    <span className="flex shrink-0 gap-1">
                      <button data-testid={`alert-snooze-${a.id}`} title="Remind me in 1 hour" onClick={(e) => snooze(a, e)}
                        className="rounded-lg p-1.5 text-[#7B6157] hover:bg-[#F5F2EB]"><Clock3 size={13} /></button>
                      <button data-testid={`alert-action-${a.id}`} title="Open" onClick={(e) => act(a, e)}
                        className="rounded-lg p-1.5 text-[#7B6157] hover:bg-[#F5F2EB]"><ExternalLink size={13} /></button>
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <button data-testid="alerts-manage-btn" onClick={() => { setOpen(false); navigate("/letters"); }}
            className="block w-full border-t border-[#F1EEE7] px-4 py-2.5 text-center text-xs font-semibold text-[#7B6157] hover:bg-[#FDFCF8] rounded-b-2xl">
            Manage alerts & client notifications
          </button>
        </div>
      )}
    </div>
  );
}
